import { z } from 'zod';
import i18n from 'i18next';
import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import { RelationshipOptions } from '@/models/common/RelationshipOptions';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { isHebrewName } from '@/helpers/hebrewName';

const err = (key: string) => ({ error: () => i18n.t(key) });

const personContactFields = {
  firstName: z
    .string()
    .min(1, err('openFundRequest.errors.firstNameRequired'))
    .refine(isHebrewName, err('hebrewNameOnly')),
  lastName: z
    .string()
    .min(1, err('openFundRequest.errors.lastNameRequired'))
    .refine(isHebrewName, err('hebrewNameOnly')),
  israeliId: z
    .string()
    .min(1, err('openFundRequest.errors.israeliIdInvalid'))
    .refine(isValidIsraeliID, err('openFundRequest.errors.israeliIdInvalid')),
  city: z.string().min(1, err('openFundRequest.errors.cityRequired')),
  street: z.string().min(1, err('openFundRequest.errors.streetRequired')),
  houseNumber: z.string().min(1, err('openFundRequest.errors.houseNumberRequired')),
  email: z.string().email(err('openFundRequest.errors.emailInvalid')),
  phone: z
    .string()
    .regex(/^0\d{8,9}$/, err('openFundRequest.errors.phoneInvalid')),
};

export const CoordinatorRelationToSupportedOptions = [
  'familyMember',
  'friendAcquaintance',
  'communityRepresentative',
  'charityTrustee',
] as const;
export type CoordinatorRelationToSupported = (typeof CoordinatorRelationToSupportedOptions)[number];

export const coordinatorSchema = z.object({
  ...personContactFields,
  relationToSupported: z.string().optional().default(''),
  idCardPhotocopy: z
    .instanceof(File, err('openFundRequest.errors.idCardRequired'))
    .nullable()
    .refine((v): v is File => !!v, err('openFundRequest.errors.idCardRequired')),
  idAppendixPhotocopy: z
    .instanceof(File, err('openFundRequest.errors.idAppendixRequired'))
    .nullable()
    .refine((v): v is File => !!v, err('openFundRequest.errors.idAppendixRequired')),
  additionalDocuments: z
    .array(
      z.object({
        name: z.string().min(1, err('openFundRequest.errors.additionalDocNameRequired')),
        file: z
          .instanceof(File, err('openFundRequest.errors.additionalDocFileRequired'))
          .nullable()
          .refine((v): v is File => !!v, err('openFundRequest.errors.additionalDocFileRequired')),
      })
    )
    .default([]),
  spouseFirstName: z.string().optional().default('').refine(isHebrewName, err('hebrewNameOnly')),
  spouseIsraeliId: z
    .string()
    .optional()
    .default('')
    .refine((v) => !v || isValidIsraeliID(v), err('openFundRequest.errors.israeliIdInvalid')),
});

export const supportedSchema = z.object({
  ...personContactFields,
  idCardPhotocopy: z
    .instanceof(File, err('openFundRequest.errors.idCardRequired'))
    .nullable()
    .refine((v): v is File => !!v, err('openFundRequest.errors.idCardRequired')),
  idAppendixPhotocopy: z
    .instanceof(File, err('openFundRequest.errors.idAppendixRequired'))
    .nullable()
    .refine((v): v is File => !!v, err('openFundRequest.errors.idAppendixRequired')),
  additionalDocuments: z
    .array(
      z.object({
        name: z.string().min(1, err('openFundRequest.errors.additionalDocNameRequired')),
        file: z
          .instanceof(File, err('openFundRequest.errors.additionalDocFileRequired'))
          .nullable()
          .refine((v): v is File => !!v, err('openFundRequest.errors.additionalDocFileRequired')),
      })
    )
    .default([]),
  relationshipToFirstSupported: z.enum(RelationshipOptions).default(RelationshipOptions.Self),
});

// A fund is run by its coordinator, optionally with a single co-coordinator alongside them.
export const MAX_COORDINATORS = 2;

const commonFormFields = {
  fundName: z.string().min(1, err('openFundRequest.errors.fundNameRequired')),
  reasonSummary: z
    .string()
    .min(1, err('openFundRequest.errors.reasonSummaryRequired')),
  coordinators: z
    .array(coordinatorSchema)
    .max(MAX_COORDINATORS, err('openFundRequest.errors.maxCoordinatorsExceeded'))
    .default([]),
  personalInterfaceInNedarimPlus: z.boolean().default(false),
  crowdfundingCampaign: z.boolean().default(false),
  termsAndConditionsAccepted: z
    .boolean()
    .refine((v) => v === true, err('openFundRequest.errors.termsRequired')),
  regulationsAccepted: z
    .boolean()
    .refine((v) => v === true, err('openFundRequest.errors.regulationsRequired')),
  signature: z.string().min(1, err('openFundRequest.errors.signatureRequired')),
};

export const MAX_WELFARE_SUPPORTEDS = 1;

export const welfareFormSchema = z
  .object({
    fundClassification: z.literal(FundClassification.Welfare),
    ...commonFormFields,
    fundGoals: z
      .array(z.enum(FundGoals))
      .min(1, err('openFundRequest.errors.fundGoalsRequired')),
    otherGoalDescription: z.string().optional().default(''),
    supporteds: z
      .array(supportedSchema)
      .min(1, err('openFundRequest.errors.supportedsRequired'))
      .max(
        MAX_WELFARE_SUPPORTEDS,
        err('openFundRequest.errors.maxSupportedsExceeded')
      ),
    isCoordinator: z.boolean().default(true),
    supportedAwareOfFund: z.boolean().default(true),
    supportedCanViewFund: z.boolean().default(false),
  })
  .refine(
    (data) =>
      !data.fundGoals.includes(FundGoals.Other) ||
      (data.otherGoalDescription ?? '').trim().length > 0,
    {
      error: () => i18n.t('openFundRequest.errors.otherGoalDescriptionRequired'),
      path: ['otherGoalDescription'],
    }
  )
  .refine((data) => data.isCoordinator || data.coordinators.length >= 1, {
    error: () => i18n.t('openFundRequest.errors.coordinatorsRequired'),
    path: ['coordinators'],
  })
  .refine(
    (data) =>
      data.coordinators.length === 0 ||
      data.coordinators.every((c) => (c.relationToSupported ?? '').trim().length > 0),
    {
      error: () => i18n.t('openFundRequest.errors.relationToSupportedRequired'),
      path: ['coordinators'],
    }
  );

export const ResponsiblePartyOptions = ['community', 'charityTrustee'] as const;
export type ResponsibleParty = (typeof ResponsiblePartyOptions)[number];

export const communityFormSchema = z.object({
  fundClassification: z.literal(FundClassification.Community),
  ...commonFormFields,
  responsibleParty: z.enum(ResponsiblePartyOptions).default('community'),
  responsibleEntityName: z
    .string()
    .min(1, err('openFundRequest.errors.responsibleEntityNameRequired')),
  fundGoals: z
    .array(z.enum(FundGoals))
    .min(1, err('openFundRequest.errors.fundGoalsRequired')),
  otherGoalDescription: z.string().optional().default(''),
  coordinators: z
    .array(coordinatorSchema)
    .min(1, err('openFundRequest.errors.coordinatorsRequired'))
    .max(MAX_COORDINATORS, err('openFundRequest.errors.maxCoordinatorsExceeded')),
}).refine(
  (data) =>
    !data.fundGoals.includes(FundGoals.Other) ||
    (data.otherGoalDescription ?? '').trim().length > 0,
  {
    error: () => i18n.t('openFundRequest.errors.otherGoalDescriptionRequired'),
    path: ['otherGoalDescription'],
  }
);

export const openFundFormSchema = z.discriminatedUnion('fundClassification', [
  welfareFormSchema,
  communityFormSchema,
]);

// ----- Edit ("completion") mode schemas -----
// When an applicant returns to complete an Incomplete request, previously-uploaded documents are
// preserved server-side, so document fields are optional here. The signature is still required
// (it is re-captured), which commonFormFields already enforces.
const coordinatorSchemaEdit = z.object({
  ...personContactFields,
  relationToSupported: z.string().optional().default(''),
  idCardPhotocopy: z.instanceof(File).nullable().optional(),
  idAppendixPhotocopy: z.instanceof(File).nullable().optional(),
  additionalDocuments: z
    .array(
      z.object({
        name: z.string().optional().default(''),
        file: z.instanceof(File).nullable().optional(),
      })
    )
    .default([]),
  spouseFirstName: z.string().optional().default('').refine(isHebrewName, err('hebrewNameOnly')),
  spouseIsraeliId: z
    .string()
    .optional()
    .default('')
    .refine((v) => !v || isValidIsraeliID(v), err('openFundRequest.errors.israeliIdInvalid')),
});

const supportedSchemaEdit = z.object({
  ...personContactFields,
  idCardPhotocopy: z.instanceof(File).nullable().optional(),
  idAppendixPhotocopy: z.instanceof(File).nullable().optional(),
  additionalDocuments: z
    .array(
      z.object({
        name: z.string().optional().default(''),
        file: z.instanceof(File).nullable().optional(),
      })
    )
    .default([]),
  relationshipToFirstSupported: z.enum(RelationshipOptions).default(RelationshipOptions.Self),
});

const welfareFormSchemaEdit = z
  .object({
    fundClassification: z.literal(FundClassification.Welfare),
    ...commonFormFields,
    coordinators: z
      .array(coordinatorSchemaEdit)
      .max(MAX_COORDINATORS, err('openFundRequest.errors.maxCoordinatorsExceeded'))
      .default([]),
    fundGoals: z.array(z.enum(FundGoals)).min(1, err('openFundRequest.errors.fundGoalsRequired')),
    otherGoalDescription: z.string().optional().default(''),
    supporteds: z
      .array(supportedSchemaEdit)
      .min(1, err('openFundRequest.errors.supportedsRequired'))
      .max(MAX_WELFARE_SUPPORTEDS, err('openFundRequest.errors.maxSupportedsExceeded')),
    isCoordinator: z.boolean().default(false),
    supportedAwareOfFund: z.boolean().default(true),
    supportedCanViewFund: z.boolean().default(false),
  })
  .refine(
    (data) =>
      !data.fundGoals.includes(FundGoals.Other) ||
      (data.otherGoalDescription ?? '').trim().length > 0,
    {
      error: () => i18n.t('openFundRequest.errors.otherGoalDescriptionRequired'),
      path: ['otherGoalDescription'],
    }
  )
  .refine((data) => data.isCoordinator || data.coordinators.length >= 1, {
    error: () => i18n.t('openFundRequest.errors.coordinatorsRequired'),
    path: ['coordinators'],
  });

const communityFormSchemaEdit = z
  .object({
    fundClassification: z.literal(FundClassification.Community),
    ...commonFormFields,
    responsibleParty: z.enum(ResponsiblePartyOptions).default('community'),
    responsibleEntityName: z.string().min(1, err('openFundRequest.errors.responsibleEntityNameRequired')),
    fundGoals: z.array(z.enum(FundGoals)).min(1, err('openFundRequest.errors.fundGoalsRequired')),
    otherGoalDescription: z.string().optional().default(''),
    coordinators: z
      .array(coordinatorSchemaEdit)
      .min(1, err('openFundRequest.errors.coordinatorsRequired'))
      .max(MAX_COORDINATORS, err('openFundRequest.errors.maxCoordinatorsExceeded')),
  })
  .refine(
    (data) =>
      !data.fundGoals.includes(FundGoals.Other) ||
      (data.otherGoalDescription ?? '').trim().length > 0,
    {
      error: () => i18n.t('openFundRequest.errors.otherGoalDescriptionRequired'),
      path: ['otherGoalDescription'],
    }
  );

export const openFundFormEditSchema = z.discriminatedUnion('fundClassification', [
  welfareFormSchemaEdit,
  communityFormSchemaEdit,
]);

export type OpenFundFormData = z.infer<typeof openFundFormSchema>;
export type WelfareFormData = z.infer<typeof welfareFormSchema>;
export type CommunityFormData = z.infer<typeof communityFormSchema>;
export type CoordinatorFormData = z.infer<typeof coordinatorSchema>;
export type SupportedFormData = z.infer<typeof supportedSchema>;

export const createDefaultCoordinator = (): CoordinatorFormData => ({
  firstName: '',
  lastName: '',
  israeliId: '',
  city: '',
  street: '',
  houseNumber: '',
  email: '',
  phone: '',
  relationToSupported: '',
  idCardPhotocopy: null as unknown as File,
  idAppendixPhotocopy: null as unknown as File,
  additionalDocuments: [],
  spouseFirstName: '',
  spouseIsraeliId: '',
});

export const createDefaultSupported = (
  isFirst: boolean = true
): SupportedFormData => ({
  firstName: '',
  lastName: '',
  israeliId: '',
  city: '',
  street: '',
  houseNumber: '',
  email: '',
  phone: '',
  idCardPhotocopy: null as unknown as File,
  idAppendixPhotocopy: null as unknown as File,
  additionalDocuments: [],
  relationshipToFirstSupported: isFirst
    ? RelationshipOptions.Self
    : RelationshipOptions.Spouse,
});

export const getDefaultValues = (
  classification: FundClassification.Welfare | FundClassification.Community
): OpenFundFormData => {
  const common = {
    fundName: '',
    reasonSummary: '',
    personalInterfaceInNedarimPlus: false,
    crowdfundingCampaign: false,
    termsAndConditionsAccepted: false,
    regulationsAccepted: false,
    signature: '',
  };

  if (classification === FundClassification.Welfare) {
    return {
      ...common,
      fundClassification: FundClassification.Welfare,
      fundGoals: [],
      otherGoalDescription: '',
      supporteds: [createDefaultSupported(true)],
      isCoordinator: true,
      supportedAwareOfFund: true,
      supportedCanViewFund: false,
      coordinators: [],
    } as OpenFundFormData;
  }

  return {
    ...common,
    fundClassification: FundClassification.Community,
    responsibleParty: 'community',
    responsibleEntityName: '',
    fundGoals: [],
    otherGoalDescription: '',
    coordinators: [createDefaultCoordinator()],
  } as OpenFundFormData;
};
